import React, { useState } from 'react';
import { Pencil, Trash, Settings, Type, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { useProductsSlice } from 'app/pages/Product/slice';
import { AlertProductAttributeDelete } from '../delete/delete-confirmation';
import { useNavigate } from 'react-router-dom';
import { useToast } from 'app/components/ui/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';

const ViewList: React.FC<{ onEdit: (attribute: any) => void }> = ({
  onEdit,
}) => {
  const {
    useLazyGetProductsAttributeTypeQuery,
    useUpdateAttributeDisplayOrderMutation,
    useUpdateMultipleAttributeDisplayOrderMutation
  } = useProductsSlice();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [openDelete, setOpenDelete] = useState(false);
  const [attributeToDelete, setAttributeToDelete] = useState(null);
  const [editingOrder, setEditingOrder] = useState<number | null>(null);
  const [tempOrder, setTempOrder] = useState<number>(0);

  const [getAttributeList, { data: attributeData, isLoading, error }] =
    useLazyGetProductsAttributeTypeQuery();

  const [updateDisplayOrder] = useUpdateAttributeDisplayOrderMutation();
  const [updateMultipleDisplayOrder] = useUpdateMultipleAttributeDisplayOrderMutation();

  React.useEffect(() => {
    getAttributeList({});
  }, [getAttributeList]);

  const addTerms = id => {
    navigate(`/products/attributes/values/${id}`);
  };

  const handleOrderEdit = (attribute: any) => {
    setEditingOrder(attribute.id);
    setTempOrder(attribute.display_order || 0);
  };

  const handleOrderSave = async (attributeId: number) => {
    try {
      await updateDisplayOrder({
        id: attributeId,
        display_order: tempOrder,
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Display order updated successfully!',
        variant: 'default',
      });

      setEditingOrder(null);
      getAttributeList({}); // Refresh the list
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update display order',
        variant: 'destructive',
      });
    }
  };

  const handleOrderCancel = () => {
    setEditingOrder(null);
    setTempOrder(0);
  };

  const moveAttribute = async (attributeId: number, direction: 'up' | 'down') => {
    const attributes = attributeData?.data || [];
    const currentIndex = attributes.findIndex(attr => attr.id === attributeId);

    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (newIndex < 0 || newIndex >= attributes.length) return;

    // Swap the display orders
    const currentAttr = attributes[currentIndex];
    const targetAttr = attributes[newIndex];

    try {
      await updateMultipleDisplayOrder({
        attributes: [
          { id: currentAttr.id, display_order: targetAttr.display_order || newIndex + 1 },
          { id: targetAttr.id, display_order: currentAttr.display_order || currentIndex + 1 }
        ]
      }).unwrap();

      toast({
        title: 'Success',
        description: 'Attribute order updated successfully!',
        variant: 'default',
      });

      getAttributeList({}); // Refresh the list
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update attribute order',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="p-6">
      {/* Empty State */}
      {!isLoading &&
        (!attributeData?.data || attributeData.data.length === 0) ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center mb-4">
            <Type className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
            No Attributes Found
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 max-w-sm">
            Start by creating your first product attribute to organize your
            products better.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <Table>
            <TableHeader className="bg-gray-50 dark:bg-gray-800/50">
              <TableRow className="border-gray-200 dark:border-gray-700">
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-center w-20">
                  Order
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-left w-auto">
                  Attribute Name
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-left w-auto">
                  Slug
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-center w-32">
                  Terms
                </TableHead>
                <TableHead className="text-gray-700 dark:text-gray-300 font-semibold text-center w-32">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attributeData?.data?.map((attribute, index) => (
                <TableRow
                  key={attribute.id}
                  className="border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors duration-200"
                >
                  <TableCell className="py-4 text-center">
                    <div className="flex items-center justify-center space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveAttribute(attribute.id, 'up')}
                        disabled={index === 0}
                        className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </Button>

                      {editingOrder === attribute.id ? (
                        <div className="flex items-center space-x-1">
                          <Input
                            type="number"
                            value={tempOrder}
                            onChange={(e) => setTempOrder(parseInt(e.target.value) || 0)}
                            className="w-12 h-6 text-xs text-center"
                            min="1"
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOrderSave(attribute.id)}
                            className="h-6 w-6 p-0 text-green-600 hover:bg-green-50"
                          >
                            ✓
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={handleOrderCancel}
                            className="h-6 w-6 p-0 text-red-600 hover:bg-red-50"
                          >
                            ✕
                          </Button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-1">
                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400 min-w-[20px]">
                            {attribute.display_order || index + 1}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOrderEdit(attribute)}
                            className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            <Pencil className="w-3 h-3" />
                          </Button>
                        </div>
                      )}

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => moveAttribute(attribute.id, 'down')}
                        disabled={index === attributeData.data.length - 1}
                        className="h-6 w-6 p-0 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900 dark:text-gray-100">
                        {attribute.name}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <code className="text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded text-gray-700 dark:text-gray-300">
                      {attribute.slug}
                    </code>
                  </TableCell>
                  <TableCell className="py-4 text-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => addTerms(attribute.id)}
                      className="h-8 px-3 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-600 transition-all duration-200"
                    >
                      <Settings className="w-4 h-4 mr-1" />
                      Configure
                    </Button>
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-center space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => onEdit(attribute)}
                        className="h-8 w-8 p-0 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:border-blue-300 dark:hover:border-blue-700"
                      >
                        <Pencil className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setAttributeToDelete(attribute);
                          setOpenDelete(true);
                        }}
                        className="h-8 w-8 p-0 hover:bg-red-50 dark:hover:bg-red-900/20 hover:border-red-300 dark:hover:border-red-700"
                      >
                        <Trash className="w-4 h-4 text-red-600 dark:text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <AlertProductAttributeDelete
        isOpen={openDelete}
        onOpenChange={() => setOpenDelete(false)}
        data={attributeToDelete}
      />
    </div>
  );
};

export default ViewList;
